import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from './api';
import { GENERIC_CONFLICT, conflictMessage } from './conflict-copy';
import { ERROR_MESSAGES } from './refusal-copy';
import { cn } from './utils';

/**
 * THE SPECIFIC CODE IS TRIED FIRST, THEN THE ENVELOPE.
 *
 * Most refusals arrive as `{ error: '<envelope>', message: '<real code>' }`.
 * This function used to read only `error`, so a 409 -- every one of them --
 * resolved to the envelope code `conflict`, matched nothing in the table
 * below, and was shown to the user as the single word "Conflict." The same
 * discard silenced the sentences already written here for `user_not_found`,
 * `country_required` and every other specific code the helpers nest: they were
 * unreachable, because `not_found` and `bad_request` matched first.
 *
 * The fix is at the edge rather than on the wire. `error` stays a coarse class
 * -- it is what a handful of call sites branch on, and what an external
 * consumer would key off -- and the reason is simply no longer thrown away.
 */
export function humanizeError(e: unknown): string {
  if (!e) return ERROR_MESSAGES.unknown!;
  if (e instanceof ApiError) return messageFor(e.specificCode, e.code, e.details);
  if (typeof e === 'string') return messageFor(e, e, null);
  if (e instanceof Error) return e.message;
  return ERROR_MESSAGES.unknown!;
}

function messageFor(
  specific: string | null,
  envelope: string,
  details: Record<string, unknown> | null,
): string {
  for (const code of specific && specific !== envelope ? [specific, envelope] : [envelope]) {
    const refusal = conflictMessage(code, details);
    if (refusal) return refusal;
    const exact = ERROR_MESSAGES[code];
    if (exact) return exact;
    const byBase = ERROR_MESSAGES[code.split(':')[0]!];
    if (byBase) return byBase;
  }
  // Never the specific code: it can be `duplicate.tenants.slug`, and the last
  // resort below would render that as "Duplicate.tenants.slug."
  if (envelope === 'conflict') return GENERIC_CONFLICT;
  return friendlyFallback(envelope);
}

function friendlyFallback(code: string): string {
  // Convert snake_case codes to Sentence case as a last resort. Parameters
  // after the first colon are dropped: `unexpected_detail:NDL` reaching a
  // person as "Unexpected detail:NDL." shows them our wire format, and the
  // part they could act on is never the part after the colon.
  const words = code.split(':')[0]!.replace(/[_-]+/g, ' ').trim();
  if (!words) return ERROR_MESSAGES.unknown!;
  return words.charAt(0).toUpperCase() + words.slice(1) + '.';
}

export function ErrorBanner({
  error,
  className,
  helpUrl,
}: {
  error: unknown | null;
  className?: string;
  /** Optional /support/* path with an article about this error. */
  helpUrl?: string;
}) {
  if (!error) return null;
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-fg',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span className="leading-snug">
        {humanizeError(error)}
        {helpUrl ? (
          <>
            {' '}
            <a
              href={helpUrl}
              className="font-medium underline underline-offset-2 hover:no-underline"
            >
              Learn more →
            </a>
          </>
        ) : null}
      </span>
    </div>
  );
}

export function PanelRefusal({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft/30 px-3 py-2 text-sm text-fg',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
      <span className="leading-snug">{message}</span>
    </div>
  );
}

export function EmptyState({
  icon = '·',
  title,
  hint,
  action,
  helpUrl,
}: {
  icon?: string;
  title: string;
  hint?: string;
  action?: ReactNode;
  /** Optional /support/* path with deeper guidance for this empty state. */
  helpUrl?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-line bg-surface/40 px-6 py-12 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-surface-2 text-2xl text-muted-foreground">
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-base font-medium text-fg">{title}</p>
        {hint ? <p className="mx-auto max-w-md text-sm text-muted-foreground">{hint}</p> : null}
        {helpUrl ? (
          <p>
            <a
              href={helpUrl}
              className="text-xs font-medium text-brand underline-offset-2 hover:underline"
            >
              Read the guide →
            </a>
          </p>
        ) : null}
      </div>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  return (
    <div className="space-y-3 py-4">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
      {label ? <p className="text-xs text-muted-foreground">{label}</p> : null}
    </div>
  );
}
